import { DateTime } from 'luxon';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Alert, Tag, useAlert } from '../../components';
import { useZendeskThread } from './use-zendesk';
import type {
  ZendeskActionResponse,
  ZendeskComment,
  ZendeskStatus,
  ZendeskTicket
} from './zendesk';
import { guardWrite, mockReply, mockStatus, zendeskCall } from './zendesk';

interface Props {
  tickets: ZendeskTicket[];
  /** The function found nothing open and fell back to recently solved tickets. */
  solvedFallback?: boolean;
  /** Keeps the table (and its header) on screen while the list is in flight. */
  loading?: boolean;
}

// Agent ticket URL base (matches the org-search host used in the Comms header).
const ZENDESK_TICKET_BASE = 'https://livecontrol.zendesk.com/agent/tickets/';

/*
 * Possible statuses (lowercase key) -> label, the pill class used by the Set
 * Status buttons, and the bare text colour the row shows on the right. The
 * colours mirror the cc-* classes so both readings of a status match; on-hold
 * takes cyan, the one accent no other status uses.
 */
const STATUS_OPTIONS: { key: ZendeskStatus; label: string; className: string; color: string }[] = [
  { key: 'open', label: 'Open', className: 'cc-amber', color: 'rgb(255, 210, 122)' },
  { key: 'monitoring', label: 'Monitoring', className: 'cc-blue', color: 'rgb(159, 176, 255)' },
  {
    key: 'investigating',
    label: 'Investigating',
    className: 'cc-purple',
    color: 'rgb(216, 180, 254)'
  },
  { key: 'onhold', label: 'On-Hold', className: 'cc-cyan', color: 'rgb(95, 214, 232)' },
  { key: 'resolved', label: 'Resolved', className: 'cc-green', color: 'rgb(127, 230, 184)' }
];

// Counter row in the table header: everything that isn't resolved.
const OPEN_STATUSES: ZendeskStatus[] = ['open', 'investigating', 'monitoring', 'onhold'];

const STATUS_MAP: Partial<Record<string, { label: string; className: string; color: string }>> =
  Object.fromEntries(STATUS_OPTIONS.map((option) => [option.key, option]));

const CHANNEL_MAP: Partial<Record<string, string>> = {
  email: 'Email',
  web: 'Web form',
  api: 'API',
  chat: 'Chat',
  voice: 'Voice'
};

/*
 * Decodes Zendesk HTML to readable text (entities → chars, <br> → newline, tags
 * stripped). DOMParser doesn't run scripts or load resources, so this is XSS-safe.
 */
const decodeHtml = (html: string): string => {
  const withBreaks = html.replace(/<br\s*\/?>/gi, '\n');

  return new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent ?? '';
};

// Sort key: when the ticket was opened — the same time the row shows.
const createdAt = (ticket: ZendeskTicket): number => {
  const created = DateTime.fromISO(ticket.createdAt);

  return created.isValid ? created.toMillis() : 0;
};

// Coarse "time ago": minutes, then hours, then days — no breakdown.
const timeAgo = (iso: string): string => {
  const created = DateTime.fromISO(iso);

  if (!created.isValid) {
    return '';
  }

  const mins = Math.max(0, Math.floor(DateTime.now().diff(created, 'minutes').minutes));

  if (mins < 60) {
    return `${mins}min ago`;
  }

  const hrs = Math.floor(mins / 60);

  if (hrs < 24) {
    return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  }

  return `${Math.floor(hrs / 24)}d ago`;
};

// The whole list is one bordered card; rows are separated by hairlines, not gaps.
const Card = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 310px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(17, 27, 51, 0.32);
  overflow: hidden;
`;

/*
 * Only the rows scroll: the header keeps the counters in view, which is the
 * point of having them. min-height: 0 lets this shrink inside the flex column
 * instead of pushing the card past its max height.
 */
const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const TableHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 11px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);

  flex-shrink: 0;

  .caption {
    font-weight: 700;
    font-size: 10px;
    letter-spacing: 0.11em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.5);
  }

  .counts {
    display: inline-flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }

  .label {
    font-weight: 500;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.6);
  }

  .value {
    margin-left: 5px;
    font-weight: 700;
    font-size: 11px;
    color: var(--white);
  }
`;

// Full-width note or status line inside the card: between groups, or in place of rows.
const Row = styled.div`
  padding: 26px;
  text-align: center;
  font-weight: 500;
  font-size: 12px;
  color: rgba(176, 186, 209, 0.55);
`;

const OtherHeading = styled.div`
  padding: 9px 15px 0;
  font-size: 11px;
  color: rgba(176, 186, 209, 0.6);
`;

const Item = styled.div`
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background var(--transition);

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }

  &:last-child {
    border-bottom: none;
  }
`;

// Bare status text on the right of a row: colour only, no pill.
const StatusText = styled.span<{ $color: string }>`
  flex-shrink: 0;
  font-weight: 600;
  font-size: 11px;
  white-space: nowrap;
  color: ${({ $color }): string => $color};
`;

const Header = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  appearance: none;
  border: none;
  cursor: pointer;
  background: transparent;
  padding: 12px 14px;

  .top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .left {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }

  .dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgb(255, 138, 147);
    flex-shrink: 0;
  }

  .title {
    font-weight: 700;
    font-size: 13px;
    color: rgb(255, 255, 255);
    overflow-wrap: anywhere;
  }

  .meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-top: 7px;
    flex-wrap: wrap;
  }

  .meta-left {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .id {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 500;
    font-size: 10px;
    color: rgba(176, 186, 209, 0.6);
  }

  .who,
  .ago {
    font-weight: 500;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.62);
  }
`;

const Content = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  padding: 13px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Loading = styled.div`
  font-size: 12px;
  color: rgba(176, 186, 209, 0.55);
`;

const Message = styled.div<{ $client: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $client }): string => ($client ? 'flex-start' : 'flex-end')};
`;

const MsgMeta = styled.div`
  margin-bottom: 4px;
  font-weight: 500;
  font-size: 10px;
  color: rgba(176, 186, 209, 0.55);
`;

type BubbleVariant = 'client' | 'lc' | 'note';

const Bubble = styled.div<{ $variant: BubbleVariant }>`
  max-width: 88%;
  border-radius: 10px;
  padding: 9px 11px;
  font-weight: 400;
  font-size: 12px;
  color: rgba(213, 221, 238, 0.92);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  line-height: 1.5;

  ${({ $variant }): string => {
    if ($variant === 'note') {
      return `
        background: rgba(242, 179, 61, 0.1);
        border: 1px solid rgba(242, 179, 61, 0.3);
      `;
    }

    if ($variant === 'client') {
      return `
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
      `;
    }

    return `
      background: rgba(78, 95, 253, 0.16);
      border: 1px solid rgba(120, 130, 255, 0.3);
    `;
  }}
`;

/*
 * Attachment thumbnails under a message. Each one links to the Zendesk file,
 * which is also the thumbnail source — non-image files just render broken
 * thumbnails, so the alt text carries the file name.
 */
const Attachments = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;

  img {
    display: block;
    width: 64px;
    height: 64px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
  }
`;

const messageVariant = (message: ZendeskComment): BubbleVariant => {
  if (message.note) {
    return 'note';
  }

  return message.role === 'Client' ? 'client' : 'lc';
};

const Composer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  margin-top: 2px;
`;

const ReplyInput = styled.textarea`
  flex: 1 1 0%;
  background: rgba(8, 14, 30, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(213, 221, 238, 0.92);
  padding: 9px 11px;
  font-weight: 400;
  font-size: 12px;
  min-height: 38px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const SendButton = styled.button`
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 12px;
  padding: 10px 14px;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;

  &:hover,
  &:focus {
    outline: none;
    opacity: 0.92;
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const StatusRow = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  align-items: center;
  padding-top: 9px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const StatusLabel = styled.span`
  font-weight: 600;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(176, 186, 209, 0.5);
  margin-right: 3px;
`;

const StatusButton = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const ZendeskLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  font-weight: 600;
  font-size: 11px;
  color: rgb(159, 176, 255);
  text-decoration: none;

  i {
    font-size: 10px;
  }

  &:hover,
  &:focus {
    outline: none;
    text-decoration: none;
  }
`;

const TicketRow = ({ ticket }: { ticket: ZendeskTicket }): ReactElement => {
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(ticket.status);
  const [statusPending, setStatusPending] = useState(false);
  const [reply, setReply] = useState('');
  const [replyPending, setReplyPending] = useState(false);
  const alert = useAlert();

  // Lazy-load the thread the first time it opens; kept cached for later toggles.
  const thread = useZendeskThread(everOpened ? ticket.id : undefined);

  const status = STATUS_MAP[currentStatus.toLowerCase()] ?? {
    label: currentStatus,
    className: 'cc-neutral',
    color: 'rgba(205, 214, 234, 0.78)'
  };

  const isHigh = ticket.priority.toLowerCase() === 'high';
  const channel = CHANNEL_MAP[ticket.channel.toLowerCase()] ?? ticket.channel;

  const toggle = (): void => {
    setOpen((value) => !value);
    setEverOpened(true);
  };

  /*
   * POSTs the status change and only moves the tag once the function confirms
   * it — a failed call leaves the ticket showing its real status plus an alert.
   */
  const changeStatus = (key: ZendeskStatus): void => {
    const blocked = guardWrite('status');

    if (blocked !== undefined) {
      alert.show(blocked);

      return;
    }

    if (statusPending || key === currentStatus.toLowerCase()) {
      return;
    }

    setStatusPending(true);

    void (async (): Promise<void> => {
      try {
        const response = await zendeskCall<ZendeskActionResponse>(
          { action: 'status', ticketId: ticket.id, status: key },
          () => mockStatus(key)
        );

        if (response.ok) {
          setCurrentStatus(response.status ?? key);
          alert.clear();
        } else {
          alert.show(response.detail ?? response.reason ?? 'Failed to update status.');
        }
      } catch (error_: unknown) {
        alert.show(error_ instanceof Error ? error_.message : 'Failed to update status.');
      } finally {
        setStatusPending(false);
      }
    })();
  };

  /*
   * Posts the reply as a comment, then reloads the thread so the new message
   * shows up with whatever Zendesk stamped on it (author, role, timestamp).
   */
  const sendReply = (): void => {
    const blocked = guardWrite('reply');

    if (blocked !== undefined) {
      alert.show(blocked);

      return;
    }

    const body = reply.trim();

    if (replyPending || body === '') {
      return;
    }

    setReplyPending(true);

    void (async (): Promise<void> => {
      try {
        const response = await zendeskCall<ZendeskActionResponse>(
          { action: 'reply', ticketId: ticket.id, body },
          mockReply
        );

        if (response.ok) {
          setReply('');
          alert.clear();
          thread.reload();
        } else {
          alert.show(response.detail ?? response.reason ?? 'Failed to send the reply.');
        }
      } catch (error_: unknown) {
        alert.show(error_ instanceof Error ? error_.message : 'Failed to send the reply.');
      } finally {
        setReplyPending(false);
      }
    })();
  };

  return (
    <Item>
      <Header type='button' onClick={toggle}>
        <div className='top'>
          <span className='left'>
            {isHigh && <span className='dot' />}
            <span className='title'>{ticket.title}</span>
          </span>
          <StatusText $color={status.color}>{status.label}</StatusText>
        </div>

        <div className='meta'>
          <span className='meta-left'>
            <span className='id'>#{ticket.num}</span>
            <span className='who'>
              {ticket.requester} · {channel}
            </span>
          </span>
          <span className='ago'>{timeAgo(ticket.createdAt)}</span>
        </div>
      </Header>

      {open && (
        <Content>
          {thread.loading && !thread.data ? (
            <Loading>Loading conversation…</Loading>
          ) : (
            (thread.data ?? []).map((message) => (
              <Message key={message.id} $client={message.role === 'Client'}>
                <MsgMeta>
                  {message.note ? 'Internal note · ' : ''}
                  {message.who} · {message.role} · {timeAgo(message.time)}
                </MsgMeta>
                <Bubble $variant={messageVariant(message)}>{decodeHtml(message.text)}</Bubble>

                {message.attachments.length > 0 && (
                  <Attachments>
                    {message.attachments.map((attachment) => (
                      <a
                        key={attachment.id}
                        href={attachment.url}
                        target='_blank'
                        rel='noreferrer'
                        title={attachment.name}
                      >
                        <img src={attachment.url} alt={attachment.name} />
                      </a>
                    ))}
                  </Attachments>
                )}
              </Message>
            ))
          )}

          <Composer>
            <ReplyInput
              placeholder='Reply…'
              value={reply}
              disabled={replyPending}
              onChange={(event): void => {
                setReply(event.target.value);
              }}
            />
            <SendButton
              type='button'
              className='cc-primary'
              disabled={replyPending || reply.trim() === ''}
              onClick={sendReply}
            >
              {replyPending ? 'Sending…' : 'Send'}
            </SendButton>
          </Composer>

          <StatusRow>
            <StatusLabel>Set Status</StatusLabel>
            {STATUS_OPTIONS.map((option) => (
              <StatusButton
                key={option.key}
                type='button'
                disabled={statusPending}
                onClick={(): void => {
                  changeStatus(option.key);
                }}
              >
                <Tag
                  className={option.key === currentStatus.toLowerCase() ? option.className : undefined}
                >
                  {option.label}
                </Tag>
              </StatusButton>
            ))}
          </StatusRow>

          {alert.message !== undefined && <Alert message={alert.message} onDismiss={alert.clear} />}

          <ZendeskLink
            href={`${ZENDESK_TICKET_BASE}${ticket.id}`}
            target='_blank'
            rel='noreferrer'
          >
            View ticket in Zendesk
            <i className='far fa-external-link' />
          </ZendeskLink>
        </Content>
      )}
    </Item>
  );
};

export const TicketList = ({
  tickets,
  solvedFallback = false,
  loading = false
}: Props): ReactElement => {
  /*
   * Split by `thisEvent`, which the list action resolves for us — it also
   * matches tickets that never mention the EID in their subject, so don't
   * re-derive it here. Each group is sorted on its own, newest first.
   */
  const [eventTickets, clientTickets] = useMemo(() => {
    const byNewest = (group: ZendeskTicket[]): ZendeskTicket[] =>
      [...group].sort((a, b) => createdAt(b) - createdAt(a));

    return [
      byNewest(tickets.filter((ticket) => ticket.thisEvent)),
      byNewest(tickets.filter((ticket) => !ticket.thisEvent))
    ];
  }, [tickets]);

  // Header counters, over every ticket on screen regardless of which group it's in.
  const counts = useMemo(() => {
    const tally: Record<string, number> = Object.fromEntries(
      OPEN_STATUSES.map((key) => [key, 0])
    );

    tickets.forEach((ticket) => {
      const key = ticket.status.toLowerCase();

      if (key in tally) {
        tally[key] += 1;
      }
    });

    return tally;
  }, [tickets]);

  const rows = (group: ZendeskTicket[]): ReactElement[] =>
    group.map((ticket) => <TicketRow key={ticket.id} ticket={ticket} />);

  return (
    <Card>
      <TableHead>
        <span className='caption'>Open tickets</span>

        <span className='counts'>
          {OPEN_STATUSES.map((key) => (
            <span key={key}>
              <span className='label'>{STATUS_MAP[key]?.label ?? key}</span>
              <span className='value'>{counts[key]}</span>
            </span>
          ))}
        </span>
      </TableHead>

      {/*
        * The header stays put through every state, so the card doesn't appear and
        * disappear underneath the counters as the request resolves.
        */}
      <Body className='cc-scroll'>
        {loading ? (
          <Row>Loading tickets…</Row>
        ) : tickets.length === 0 ? (
          <Row>No open Zendesk tickets for this event or client.</Row>
        ) : (
          <>
            {solvedFallback && (
              <OtherHeading>
                Nothing open for this client — showing recently solved tickets.
              </OtherHeading>
            )}

            {rows(eventTickets)}

            {/* Only worth a heading when both groups are on screen. */}
            {eventTickets.length > 0 && clientTickets.length > 0 && (
              <OtherHeading>Other open tickets for this client</OtherHeading>
            )}

            {rows(clientTickets)}
          </>
        )}
      </Body>
    </Card>
  );
};
